import React, { useMemo, useState } from "react";
import { Box, Card, Flex, Text, Button } from "@radix-ui/themes";
import { BarChart3, Plus, GripVertical, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PaginatedResult } from "@/types/breeze/Any";
import { ChartConfiguration } from "@/types/breeze/ChartConfiguration";
import { chartConfigurationService } from "@/services/breeze/ChartConfigurationService";
import ChartRenderer from "./ChartRenderer";
import { CHART_TYPE_LABEL } from "./chartOptions";

// Sortable Chart Card Component
interface SortableChartCardProps {
  chart: ChartConfiguration;
  entityData: PaginatedResult<Record<string, unknown>> | null;
}

function SortableChartCard({ chart, entityData }: SortableChartCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chart.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 999 : 1,
    scale: isDragging ? "1.02" : "1",
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={isDragging ? "cursor-grabbing" : "cursor-grab"}
    >
      <Box p="4">
        <Flex justify="between" align="center" mb="3">
          <Flex align="center" gap="2">
            <div
              {...attributes}
              {...listeners}
              className="p-1 -m-1 rounded hover:bg-gray-100 cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </div>
            <Flex direction="column">
              <Text size="2" weight="medium" mb="1">
                {chart.name}
              </Text>
              <Text size="1" color="gray">
                {CHART_TYPE_LABEL[chart.chartType]}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <SavedChartBody chart={chart} entityData={entityData} />
      </Box>
    </Card>
  );
}

// Server-aggregated charts (yAgg set) render from previewChartConfig; legacy raw-plot charts fall back to entityData.
function SavedChartBody({
  chart,
  entityData,
}: {
  chart: ChartConfiguration;
  entityData: PaginatedResult<Record<string, unknown>> | null;
}) {
  const needsServerData = chart.dataSource !== "entity" || (!!chart.yAgg && !!chart.xField);

  const { data: aggregated, isFetching } = useQuery({
    queryKey: ["saved-chart-preview", chart.id, chart],
    queryFn: () =>
      chartConfigurationService.previewChartConfig({
        name: chart.name,
        dataSource: chart.dataSource,
        chartType: chart.chartType,
        entityName: chart.entityName,
        xField: chart.xField,
        yField: chart.yField,
        pieMode: chart.pieMode,
        stackBy: chart.stackBy,
        yAgg: chart.yAgg,
        xInterval: chart.xInterval,
        timeframePreset: chart.timeframePreset,
        timeframeFrom: chart.timeframeFrom,
        timeframeTo: chart.timeframeTo,
        limit: chart.limit,
        sortDirection: chart.sortDirection,
        cumulative: chart.cumulative,
        platformMetric: chart.platformMetric,
        platformPeriod: chart.platformPeriod,
        quotaMetric: chart.quotaMetric,
      }),
    enabled: needsServerData,
    staleTime: 30_000,
  });

  const aggregatedData = useMemo(() => {
    if (!needsServerData || !aggregated) return null;
    const preview = aggregated as { items?: Record<string, unknown>[]; total?: number };
    return {
      items: preview.items ?? [],
      total_items: preview.total ?? 0,
      page: 1,
      page_size: preview.items?.length ?? 0,
      total_pages: 1,
      has_next_page: false,
      has_previous_page: false,
    } as PaginatedResult<Record<string, unknown>>;
  }, [aggregated, needsServerData]);

  const previewChart = useMemo(() => {
    if (!needsServerData || !aggregated) return chart;
    const preview = aggregated as {
      chart?: { x_field?: string | null; y_field?: string | null; series?: string[] };
    };
    return {
      ...chart,
      xField: preview.chart?.x_field ?? chart.xField ?? null,
      yField: preview.chart?.y_field ?? chart.yField ?? null,
      series: preview.chart?.series,
    };
  }, [chart, aggregated, needsServerData]);

  const renderData = needsServerData ? aggregatedData : entityData;

  return (
    <>
      {needsServerData && isFetching && !aggregatedData ? (
        <Flex align="center" justify="center" style={{ height: "300px" }}>
          <Text size="2" color="gray">Loading…</Text>
        </Flex>
      ) : (
        <ChartRenderer chart={previewChart} entityData={renderData} height="300px" />
      )}
      <Flex justify="between" align="center" mt="2">
        <Text size="1" color="gray">
          {chart.xField} × {chart.yField || chart.yAgg || "count"}
        </Text>
        <Text size="1" color="gray">
          {needsServerData
            ? `${aggregatedData?.items?.length ?? 0} intervals`
            : `${entityData?.items?.length || 0} records`}
        </Text>
      </Flex>
    </>
  );
}

interface ChartDashboardProps {
  entityData: PaginatedResult<Record<string, unknown>> | null;
  savedCharts: ChartConfiguration[];
  showAddChart: boolean;
  onCreateChart: () => void;
  onCloseAddChart: () => void;
}

export default function ChartDashboard({
  entityData,
  savedCharts,
  showAddChart,
  onCreateChart,
  onCloseAddChart,
}: ChartDashboardProps) {
  const [dashboardCharts, setDashboardCharts] = useState<ChartConfiguration[]>(
    () => {
      // Initialize with first 4 saved charts
      return savedCharts.slice(0, 4);
    },
  );

  // Drag and Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setDashboardCharts((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addChartToDashboard = (chart: ChartConfiguration) => {
    setDashboardCharts((prev) => [...prev, chart]);
    onCloseAddChart();
  };

  // Update dashboard when saved charts change (reset on entity switch)
  React.useEffect(() => {
    setDashboardCharts(savedCharts.slice(0, 4));
  }, [savedCharts]);

  if (!entityData?.items?.length) {
    return (
      <Card>
        <Box p="6">
          <Flex direction="column" gap="4">
            <BarChart3 className="w-16 h-16 text-gray-400" />
            <Flex direction="column">
              <Text size="4" weight="medium" mb="2">
                No data available
              </Text>
              <Text size="2" color="gray">
                Add some data to this entity to create charts
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Card>
    );
  }

  if (savedCharts.length === 0) {
    return (
      <Card>
        <Box p="6">
          <Flex direction="column" gap="4">
            <BarChart3 className="w-16 h-16 text-gray-400" />
            <Flex direction="column">
              <Text size="4" weight="medium" mb="2">
                No charts yet
              </Text>
              <Text size="2" color="gray" mb="3">
                Create your first chart to start building your dashboard
              </Text>
            </Flex>
            <Box>
              <Button size="3" onClick={onCreateChart}>
                <Plus className="w-4 h-4" />
                Create My First Chart
              </Button>
            </Box>
          </Flex>
        </Box>
      </Card>
    );
  }

  return (
    <Box>
      {/* Dashboard Controls */}
      <Flex justify="between" align="center" mb="4">
        <Text size="3" weight="medium">
          Dashboard ({dashboardCharts.length} charts)
        </Text>
      </Flex>

      {showAddChart && (
        <Card style={{ marginBottom: "1rem" }}>
          <Box p="4">
            <Flex justify="between" align="center" mb="3">
              <Text size="2" weight="medium">
                Add Chart to Dashboard
              </Text>
              <Button size="1" variant="ghost" onClick={onCloseAddChart}>
                <X className="w-4 h-4" />
              </Button>
            </Flex>

            <Flex gap="2" mb="4">
              <Button
                variant="solid"
                size="2"
                onClick={() => {
                  onCloseAddChart();
                  onCreateChart();
                }}
                style={{ flex: 1 }}
              >
                <Plus className="w-4 h-4" />
                Create New Chart
              </Button>
            </Flex>

            {savedCharts.filter(
              (chart) => !dashboardCharts.find((dc) => dc.id === chart.id),
            ).length === 0 ? (
              <Box
                p="4"
                style={{
                  backgroundColor: "var(--gray-2)",
                  borderRadius: "6px",
                }}
              >
                <Flex direction="column" gap="2">
                  <Text size="2" weight="medium">
                    All charts added
                  </Text>
                  <Text size="1" color="gray">
                    All your saved charts are already on the dashboard
                  </Text>
                </Flex>
              </Box>
            ) : (
              <>
                <Text size="1" color="gray" mb="2">
                  Or add existing charts:
                </Text>
                <div
                  className="grid grid-cols-1 gap-2"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {savedCharts
                    .filter(
                      (chart) =>
                        !dashboardCharts.find((dc) => dc.id === chart.id),
                    )
                    .map((chart) => (
                      <Card key={chart.id} style={{ cursor: "pointer" }}>
                        <Box p="3" onClick={() => addChartToDashboard(chart)}>
                          <Flex justify="between" align="center">
                            <Flex direction="column">
                              <Text size="2" weight="medium" mb="1">
                                {chart.name}
                              </Text>
                              <Text size="1" color="gray">
                                {CHART_TYPE_LABEL[chart.chartType]}
                              </Text>
                            </Flex>
                            <Button size="1" variant="outline">
                              Add
                            </Button>
                          </Flex>
                        </Box>
                      </Card>
                    ))}
                </div>
              </>
            )}
          </Box>
        </Card>
      )}

      {dashboardCharts.length === 0 ? (
        <Card>
          <Box p="6">
            <Flex direction="column" gap="4">
              <BarChart3 className="w-12 h-12 text-gray-400" />
              <Box>
                <Text size="3" weight="medium" mb="2">
                  Empty Dashboard
                </Text>
                <Text size="2" color="gray" mb="3">
                  Add charts to your dashboard to start visualizing data
                </Text>
              </Box>
              <Box>
                <Text size="1" color="gray">
                  Click &ldquo;Add Chart&rdquo; above to get started
                </Text>
              </Box>
            </Flex>
          </Box>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={dashboardCharts.map((chart) => chart.id!)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboardCharts.map((chart) => (
                <SortableChartCard
                  key={chart.id}
                  chart={chart}
                  entityData={entityData}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
}
